import { useEffect, useState, useCallback, useRef } from 'react';
import { JobSkillsService } from '../services/job-skills.service';
import { Skill, TechnicalSkills } from '../models/job.model';
import { JobSkillSuggestionsTrigger } from './JobSkillSuggestionsModal';
import { Button } from '../../../../../core/components';
import {
  AutoComplete,
  Modal,
  Tag,
  Input as AntInput,
  Button as AntButton,
  Spin,
  Tooltip,
} from 'antd';
import {
  CloseCircleOutlined,
  SearchOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { UtilService } from '../../../../../core/services/util.service';

interface JobSkillsProps {
  skills: Skill[];
  setSkills: React.Dispatch<React.SetStateAction<Skill[]>>;
  isDeleted?: boolean;
  description?: string;
  jobTitle?: string;
  category?: string;
  jobId?: string;
  heading?: string | null;
}

const levelOptions = [
  'beginner',
  'intermediate',
  'advanced',
  'expert',
] as const;
type SkillLevel = (typeof levelOptions)[number];

const levelColors: Record<SkillLevel, string> = {
  beginner: 'green',
  intermediate: 'cyan',
  advanced: 'blue',
  expert: 'purple',
};

const JobSkills: React.FC<JobSkillsProps> = ({
  skills,
  setSkills,
  isDeleted,
  description = '',
  jobTitle,
  category,
  jobId,
  heading = 'Skills',
}) => {
  const [selectedSkillLevel, setSelectedSkillLevel] = useState<string>('');
  const [visibleSkillCount, setVisibleSkillCount] = useState(5);
  const [skillSearchTerm, setSkillSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [technicalSkills, setTechnicalSkills] = useState<TechnicalSkills[]>([]);
  const [skillSearched, setSkillSearched] = useState(false);
  const [skillFound, setSkillFound] = useState(true);
  const [filteredTechnicalSkills, setFilteredTechnicalSkills] = useState<
    TechnicalSkills[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const GetTechnicalSkills = useCallback(async (searchTerm?: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsLoading(true);
    setTechnicalSkills([]);
    setFilteredTechnicalSkills([]);
    const params = {
      ...(searchTerm ? { filter: `name:like:${searchTerm}` } : {}),
    };
    try {
      const technicalSkills = await JobSkillsService.Get(params, {
        signal: controller.signal,
      });
      if (technicalSkills) {
        const mappedData = technicalSkills.content.map((technicalSkill) => ({
          ...technicalSkill,
          name: technicalSkill.name,
        }));

        setTechnicalSkills(mappedData);
        setFilteredTechnicalSkills(mappedData);
      }
    } catch (error) {
      console.error('Failed to fetch technical skills:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!skillSearchTerm) {
      setFilteredTechnicalSkills([]);
      return;
    }
    const handler = setTimeout(() => {
      GetTechnicalSkills(skillSearchTerm);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [skillSearchTerm, GetTechnicalSkills]);

  useEffect(() => {
    const trimmedTerm = skillSearchTerm.trim().toLowerCase();
    if (!trimmedTerm) {
      setSkillSearched(false);
      setSkillFound(false);
      setFilteredTechnicalSkills([]);
      return;
    }
    const filtered = technicalSkills.filter((skill) =>
      skill.name.toLowerCase().includes(trimmedTerm)
    );
    setFilteredTechnicalSkills(filtered);
    setSkillSearched(true);
  }, [skillSearchTerm, technicalSkills]);

  const handleCancel = () => {
    setShowModal(false);
    setSkillSearched(false);
    setSkillSearchTerm('');
    setFilteredTechnicalSkills(technicalSkills);
  };

  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleAdd = () => {
    const trimmedSkill = skillSearchTerm.trim();
    if (
      trimmedSkill &&
      !skills.some(
        (s) =>
          s.skillName.toLowerCase() === trimmedSkill.toLowerCase() &&
          s.skillLevel === selectedSkillLevel
      )
    ) {
      const newSkill = {
        id: '',
        skillName: trimmedSkill,
        skillLevel: selectedSkillLevel,
      };
      const updatedSkills = [...skills, newSkill];
      setSkills(updatedSkills);
      setSkillSearchTerm('');
      setSkillSearched(false);
      setFilteredTechnicalSkills(technicalSkills);
      setShowModal(false);
    }
  };

  const handleDelete = (removedName: string, removedLevel: string) => {
    const updatedSkills = skills.filter(
      (skill) =>
        !(skill.skillName === removedName && skill.skillLevel === removedLevel)
    );
    setSkills(updatedSkills);
  };

  const handleCreate = useCallback(async () => {
    const trimmedName = skillSearchTerm.trim();
    if (!trimmedName) return;

    try {
      const newSkill = new TechnicalSkills(trimmedName);
      const createdSkill = await JobSkillsService.Post(newSkill);
      UtilService.Alert('Success!', 'success', 'Skill created successfully.');

      setTechnicalSkills((prev) => [...prev, createdSkill]);
      setFilteredTechnicalSkills((prev) => [...prev, createdSkill]);
      setSkillFound(true);
      setSkillSearchTerm(trimmedName);
    } catch (error: any) {
      console.error('Failed to create skill:', error);
      const errorMessage =
        error?.message || 'Failed to create skill. Please try again.';
      UtilService.Alert('Error!', 'error', errorMessage);
    }
  }, [skillSearchTerm]);

  const isButtonDisabled = !skillFound || selectedSkillLevel === '';

  return (
    <div className="flex flex-col items-start space-y-4">
      <div className="flex w-full flex-wrap items-center gap-3">
        {heading ? (
          <h2 className="text-lg text-gray-700 text-left font-medium">
            {heading}
          </h2>
        ) : null}
        {!isDeleted && (
          <JobSkillSuggestionsTrigger
            description={description}
            jobTitle={jobTitle}
            category={category}
            jobId={jobId}
            existingSkills={skills}
            setSkills={setSkills}
          />
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {skills.slice(0, visibleSkillCount).map((skill) => (
          <Tooltip
            key={`${skill.skillName}-${skill.skillLevel}`}
            title={`${skill.skillLevel.charAt(0).toUpperCase() + skill.skillLevel.slice(1)}`}
            placement="top"
          >
            <Tag
              color={levelColors[skill.skillLevel.toLowerCase() as SkillLevel]}
              closable
              onClose={
                !isDeleted
                  ? () => handleDelete(skill.skillName, skill.skillLevel)
                  : undefined
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '32px',
                padding: '0 10px',
                fontSize: '14px',
                lineHeight: '1',
              }}
            >
              {skill.skillName}
            </Tag>
          </Tooltip>
        ))}
        {skills.length > visibleSkillCount && (
          <AntButton
            type="dashed"
            onClick={() => setVisibleSkillCount(visibleSkillCount + 5)}
            className="custom-hover-bg text-blue-600 border-blue-300 bg-transparent rounded-md px-3 py-1 text-sm"
          >
            Show More
          </AntButton>
        )}
        {!showModal && !isDeleted && (
          <AntButton
            type="dashed"
            onClick={handleShowModal}
            className="custom-hover-bg text-blue-600 border-blue-300 bg-transparent rounded-md px-3 py-1 text-sm"
          >
            Add New
          </AntButton>
        )}
      </div>
      {showModal && (
        <Modal
          centered
          title={
            <div className="mb-6 w-full text-center text-lg font-bold">
              New Skill
            </div>
          }
          open={showModal}
          width={380}
          closable={false}
          footer={
            <div className="mt-8 flex flex-row justify-end space-x-4">
              <Button
                icon={{ value: <CloseCircleOutlined />, size: 'text-lg' }}
                text={{ value: 'Cancel', weight: 500, size: 'text-md' }}
                color="red"
                tone={500}
                onClick={handleCancel}
              />
              <Button
                icon={{ value: <PlusCircleOutlined />, size: 'text-lg' }}
                text={{ value: 'Add Skill', weight: 500, size: 'text-sm' }}
                color="blue"
                tone={500}
                disabled={isButtonDisabled}
                onClick={handleAdd}
              />
            </div>
          }
        >
          <div className="flex flex-col space-y-6">
            <AutoComplete
              style={{ width: 'full' }}
              value={skillSearchTerm}
              onChange={(value) => {
                setSkillSearchTerm(value);
              }}
              options={Array.from(
                new Map(
                  filteredTechnicalSkills.map((skill) => [skill.name, skill])
                ).values()
              ).map((skill) => ({
                label: skill.name,
                value: skill.name,
              }))}
              onSelect={(value) => {
                setSkillFound(true);
                setSkillSearched(true);
                setSkillSearchTerm(value);
              }}
            >
              <AntInput
                suffix={
                  isLoading ? (
                    <Spin size="small" />
                  ) : (
                    <SearchOutlined className="text-lg text-gray-500" />
                  )
                }
                placeholder="Search technical skill..."
              />
            </AutoComplete>
            {skillSearched && (
              <>
                {skillFound && (
                  <div className="flex flex-col space-y-4 items-start">
                    <span className="text-sm">Select skill level</span>
                    <div className="flex flex-row space-x-4 items-center">
                      {levelOptions.map((level) => (
                        <Tag
                          key={level}
                          color={
                            selectedSkillLevel === level
                              ? levelColors[level]
                              : 'default'
                          }
                          onClick={() => setSelectedSkillLevel(level)}
                          style={{
                            cursor: 'pointer',
                            borderRadius: 0,
                            margin: 0,
                            fontSize: '14px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: '30px',
                            padding: '10px',
                          }}
                        >
                          {level.charAt(0).toUpperCase() + level.slice(1)}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
                <div className="min-h-[40px]">
                  {filteredTechnicalSkills.length === 0 && (
                    <div className=" flex flex-row justify-between items-center">
                      <span className="text-gray-700 text-base">
                        Skill not found. Create it?
                      </span>
                      <Button
                        text={{
                          value: 'Create Skill',
                          weight: 500,
                          size: 'text-sm',
                        }}
                        color="blue"
                        tone={500}
                        onClick={handleCreate}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default JobSkills;
